import React from 'react';
import {Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography} from '@mui/material';
import {ArchiveOutlined, Close, RestoreOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {SettingsDialog} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';

export function CampaignDialog({sx = [], ...props}) {
    return <SettingsDialog {...props} sx={[{
        '& .MuiDialogContent-root': {position: 'relative', minWidth: 0},
        '& .MuiDialogTitle-root + .MuiDialogContent-root': {pt: 1.5},
        '& .MuiDialog-paper': {
            '& .MuiTextField-root': {my: 0, minWidth: 0},
            '& .MuiOutlinedInput-root': {bgcolor: theme => getSettingsColors(theme).input},
            '& .MuiOutlinedInput-input': {fontSize: 13},
            '& .MuiButton-root': {textTransform: 'none', boxShadow: 'none', minHeight: 36},
            '& .MuiAlert-root': {borderRadius: '10px', fontSize: 13, lineHeight: 1.6},
            '& h3': {fontSize: 15, fontWeight: 600, margin: '0 0 12px'},
            '& p, & li': {fontSize: 13, lineHeight: 1.7},
            '& ul, & ol': {paddingInlineStart: 20}
        }
    }, ...(Array.isArray(sx) ? sx : [sx])]}/>;
}

export function CampaignDialogHeader({id, title, description, icon: Icon, onClose}) {
    return <DialogTitle id={id}>
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
            {Icon && <Box sx={{display: 'flex', p: 1, mt: 0.25, borderRadius: '10px', color: 'primary.main',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.1)}}><Icon/></Box>}
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography component="span" sx={{fontSize: 'inherit', fontWeight: 'inherit'}}>{title}</Typography>
                {description && <Typography sx={{mt: 0.75, fontSize: 13, color: 'text.secondary', lineHeight: 1.6, overflowWrap: 'anywhere'}}>{description}</Typography>}
            </Box>
            {onClose && <Tooltip title="Close"><IconButton aria-label="Close dialog" size="small" onClick={onClose} sx={{color: 'text.secondary'}}><Close fontSize="small"/></IconButton></Tooltip>}
        </Box>
    </DialogTitle>;
}

export function CampaignSection({title, description, children}) {
    return <Box component="section" sx={{p: {xs: 2, sm: 2.5}, minWidth: 0, borderRadius: '12px',
        bgcolor: theme => getSettingsColors(theme).inset}}>
        {title && <Typography component="h3" sx={{fontSize: 15, fontWeight: 600, mb: 1}}>{title}</Typography>}
        {description && <Typography sx={{fontSize: 13, color: 'text.secondary', mb: 2, lineHeight: 1.6}}>{description}</Typography>}
        {children}
    </Box>;
}

export function CampaignConfirmationDialog({confirm, action = 'archive', selectedCount = 1}) {
    const restoring = action === 'restore';
    const Icon = restoring ? RestoreOutlined : ArchiveOutlined;
    const label = restoring ? 'Restore' : 'Archive';
    const campaigns = selectedCount === 1 ? 'campaign' : `${selectedCount} campaigns`;
    const id = React.useId();
    return <CampaignDialog open fullWidth maxWidth="xs" onClose={() => confirm(false)}
        aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
        sx={{'& .MuiDialog-paper': {borderRadius: '16px'}}}>
        <DialogTitle id={`${id}-title`}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                <Box sx={{display: 'flex', p: 1, borderRadius: '10px', color: restoring ? 'primary.main' : 'error.main',
                    bgcolor: theme => alpha(restoring ? theme.palette.primary.main : theme.palette.error.main, 0.1)}}><Icon/></Box>
                <Box component="span">{label} {campaigns}?</Box>
            </Box>
        </DialogTitle>
        <DialogContent><Typography id={`${id}-description`} sx={{fontSize: 14, color: 'text.secondary', lineHeight: 1.7}}>
            {restoring ? 'The selected campaigns will return to your active campaigns.' :
                'The selected campaigns will move to your archive. You can restore them from “View archived” at any time.'}
        </Typography></DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={() => confirm(false)}>Cancel</Button>
            <Button variant="contained" color={restoring ? 'primary' : 'error'} onClick={() => confirm(true)}>{label} {selectedCount === 1 ? 'campaign' : 'campaigns'}</Button>
        </DialogActions>
    </CampaignDialog>;
}
